/**
 * The price grid — recording several suppliers' prices for many lines in one pass (Akhil,
 * 2026-09-16: "for 100 line items we need to add price for 5–10 items … given by 4–5, 10, n
 * suppliers; updating and searching each item then adding the quote is a lengthy process").
 *
 * Rows are the lines that can take a supplier price (RFQ-routed, matched to an item); columns
 * are suppliers — ANY supplier. The ones already on the lines, the RFQs and the existing
 * quotations are offered first, but a column can be any supplier at all: a quote that arrives
 * from someone never sent an RFQ is still a quote, and the comparison must see it. Whether an RFQ
 * went to a supplier is carried per row as a hint (`sentTo`), never a restriction.
 *
 * A blank cell is "did not quote"; only filled cells are saved. Each column becomes one ordinary
 * Supplier Quotation (`buildManualQuotation`), so nothing downstream changes.
 */

import { printedDescription } from './internalNotes'
import { IN_HOUSE_SUPPLIER } from './directPricing'
import { editedDescription, isRfqLine } from './rfq'
import type { EnquiryItem, Money, Rfq, SupplierQuotation } from './types'

export interface GridRow {
  line_id: string
  cust_sno: string
  item_code: string
  /** The line as the purchaser wrote it — see `gridRows`. */
  name: string
  /** The catalogue's name for the item, shown beneath `name` when it differs — so "general paint"
   *  and `GENERAL 25 - MARINE PAINT` are both on the row (Akhil, 2026-10-16). */
  master: string
  qty: number
  uom: string
  /** What each supplier has already quoted for this item, by supplier code. */
  quoted: Record<string, Money>
  /** Suppliers whose RFQ carried this line — a hint on the cell. */
  sentTo: Set<string>
}

export function gridRows(
  lines: readonly EnquiryItem[],
  quotations: readonly SupplierQuotation[],
  rfqs: readonly Rfq[],
  nameByCode: ReadonlyMap<string, string> = new Map(),
): GridRow[] {
  const quotedByCode = new Map<string, Record<string, Money>>()
  for (const sq of quotations) {
    for (const item of sq.items) {
      const rec = quotedByCode.get(item.item_code) ?? {}
      rec[sq.supplier] = item.rate
      quotedByCode.set(item.item_code, rec)
    }
  }
  const sentByRow = new Map<string, Set<string>>()
  for (const rfq of rfqs) {
    for (const item of rfq.items ?? []) {
      const set = sentByRow.get(item.enquiry_row_no) ?? new Set<string>()
      set.add(rfq.supplier)
      sentByRow.set(item.enquiry_row_no, set)
    }
  }
  return lines
    .filter((l): l is EnquiryItem & { item_code: string } => isRfqLine(l) && !!l.item_code)
    .sort((a, b) => Number(a.cust_sno) - Number(b.cust_sno) || a.cust_sno.localeCompare(b.cust_sno))
    .map((l) => ({
      line_id: l.line_id,
      cust_sno: l.cust_sno,
      item_code: l.item_code,
      // The purchaser's description first (Akhil, 2026-10-16: "general paint" written over a master
      // name is what they are pricing), then the catalogue name, then the code.
      name: editedDescription(l) || nameByCode.get(l.item_code) || printedDescription(l) || l.item_code,
      master: nameByCode.get(l.item_code) ?? '',
      qty: l.qty,
      uom: l.uom,
      quoted: quotedByCode.get(l.item_code) ?? {},
      sentTo: sentByRow.get(l.cust_sno) ?? new Set<string>(),
    }))
}

/** Every supplier in play, plus any added by hand — sorted, the in-house marker excluded. */
export function gridColumns(
  lines: readonly EnquiryItem[],
  quotations: readonly SupplierQuotation[],
  rfqs: readonly Rfq[],
  extra: readonly string[] = [],
): string[] {
  const set = new Set<string>()
  for (const l of lines) for (const s of l.suppliers) set.add(s)
  for (const sq of quotations) set.add(sq.supplier)
  for (const rfq of rfqs) set.add(rfq.supplier)
  for (const s of extra) set.add(s)
  set.delete(IN_HOUSE_SUPPLIER)
  set.delete('')
  return [...set].sort((a, b) => a.localeCompare(b))
}

export interface GridEntry {
  supplier: string
  currency: string
  rates: Map<string, number>
  /** `line_id` → what the supplier said beyond the number (2026-09-18) — only for priced lines. */
  remarks: Map<string, string>
}

/** One entry per column that holds at least one rate — the input `buildManualQuotation` takes. */
export function entriesFromCells(
  cells: ReadonlyMap<string, ReadonlyMap<string, number>>,
  currencyByColumn: ReadonlyMap<string, string>,
  remarksByColumn: ReadonlyMap<string, ReadonlyMap<string, string>> = new Map(),
): GridEntry[] {
  const out: GridEntry[] = []
  for (const [supplier, rates] of cells) {
    if (rates.size === 0) continue
    const remarks = new Map<string, string>()
    for (const [lineId, remark] of remarksByColumn.get(supplier) ?? []) {
      if (rates.has(lineId) && remark.trim() !== '') remarks.set(lineId, remark.trim())
    }
    out.push({ supplier, currency: currencyByColumn.get(supplier) ?? '', rates: new Map(rates), remarks })
  }
  return out
}
